// admin/dashboard.js — painel principal do administrador.
//
// Origem (monólito): linhas 4867-5100 (HTML #adminPanel — layout raiz),
//   estrutura de navegação adaptada para o sistema de rotas v19.
//
// Dependências:
//   - auth   (core/auth.js)  — auth.getCurrent(), auth.primeiroNome(), auth.logout()
//   - router (core/router.js)— router.go() para navegação entre seções admin
//
// Exporta:
//   function render(container) — monta o dashboard do admin no elemento recebido.
//
// Notas v19:
//   - Cards/atalhos para todas as sub-rotas admin: /admin/frequencia, /admin/lancar,
//     /admin/conteudo, /admin/importar, /admin/financeiro.
//   - Saudação "Olá, Administrador" com botão de logout.
//   - Não reproduz a lógica de toggles de visibilidade — isso fica em conteudo.js.

import { auth }   from '../core/auth.js';
import { router } from '../core/router.js';
import { api }    from '../core/api.js';
import { notify } from '../core/notify.js';

// ── Helpers internos ─────────────────────────────────────────────────────────

/**
 * Gera HTML de um card de acesso rápido para o dashboard.
 * @param {string} icone    - emoji representando a seção
 * @param {string} titulo   - nome da seção
 * @param {string} descricao- descrição curta
 * @param {string} rota     - rota de destino (ex.: '/admin/frequencia')
 * @param {string} cor      - cor de destaque do card
 */
function _card(icone, titulo, descricao, rota, cor = '#6A001F') {
  return `
    <button
      class="admin-dash-card"
      type="button"
      data-rota="${rota}"
      style="
        display:flex;align-items:flex-start;gap:14px;
        background:#fff;border:none;border-radius:16px;
        padding:20px;cursor:pointer;text-align:left;width:100%;
        box-shadow:0 2px 12px rgba(0,0,0,0.07);
        transition:transform 0.15s,box-shadow 0.15s;
      "
    >
      <div style="
        width:48px;height:48px;border-radius:14px;
        background:${cor}1A;display:flex;align-items:center;
        justify-content:center;font-size:22px;flex-shrink:0;
      ">${icone}</div>
      <div style="flex:1;min-width:0">
        <div style="font-size:15px;font-weight:800;color:#0A0A0A;margin-bottom:4px">${titulo}</div>
        <div style="font-size:12px;font-weight:500;color:#6B6B6B;line-height:1.4">${descricao}</div>
      </div>
      <div style="color:#9B9B9B;font-size:18px;align-self:center">›</div>
    </button>`;
}

// ── render principal ──────────────────────────────────────────────────────────

/**
 * Renderiza o dashboard do administrador.
 * @param {HTMLElement} container - elemento raiz (ex.: #app)
 */
export function render(container) {
  const user = auth.getCurrent();
  const nome  = auth.primeiroNome(user) || 'Administrador';

  container.innerHTML = `
    <div style="
      min-height:100vh;background:#F5F5F5;
      font-family:'Montserrat',sans-serif;-webkit-font-smoothing:antialiased;
    ">
      <!-- Header -->
      <header style="
        background:linear-gradient(90deg,#6A001F 0%,#B3002D 100%);
        padding:calc(env(safe-area-inset-top,0px) + 20px) 20px 20px;
        position:sticky;top:0;z-index:100;
      ">
        <div style="display:flex;align-items:center;justify-content:space-between">
          <div>
            <div style="font-size:10px;font-weight:600;color:rgba(255,255,255,0.65);letter-spacing:2px;text-transform:uppercase">
              Eloquence
            </div>
            <div style="font-size:22px;font-weight:800;color:#fff;line-height:1.1">
              Olá, ${nome}
            </div>
          </div>
          <div style="display:flex;gap:8px;align-items:center">
            <button
              id="adminRefreshBtn"
              type="button"
              title="Recarregar dados da planilha"
              style="
                background:rgba(255,255,255,0.18);border:none;border-radius:12px;
                color:#fff;font-family:'Montserrat',sans-serif;font-size:12px;
                font-weight:700;padding:8px 12px;cursor:pointer;
                transition:background 0.2s;display:inline-flex;align-items:center;gap:6px;
              "
            ><span class="admin-refresh-icon" aria-hidden="true">↻</span><span>Atualizar</span></button>
            <button
              id="adminLogoutBtn"
              type="button"
              style="
                background:rgba(255,255,255,0.18);border:none;border-radius:12px;
                color:#fff;font-family:'Montserrat',sans-serif;font-size:12px;
                font-weight:700;padding:8px 14px;cursor:pointer;
                transition:background 0.2s;
              "
            >Sair</button>
          </div>
        </div>

        <!-- Badge de perfil -->
        <div style="
          display:flex;align-items:center;gap:8px;
          background:rgba(255,255,255,0.12);border-radius:12px;
          padding:10px 14px;margin-top:16px;
        ">
          <div style="
            width:36px;height:36px;border-radius:50%;
            background:rgba(255,255,255,0.25);
            display:flex;align-items:center;justify-content:center;
            font-size:14px;font-weight:900;color:#fff;flex-shrink:0;
          ">A</div>
          <div>
            <div style="font-size:13px;font-weight:700;color:#fff">Administrador Master</div>
            <div style="font-size:11px;color:rgba(255,255,255,0.65)">Acesso total ao sistema</div>
          </div>
        </div>
      </header>

      <!-- Conteúdo -->
      <main style="padding:20px;padding-bottom:40px">

        <!-- Seção: Frequência -->
        <div style="margin-bottom:8px">
          <p style="font-size:11px;font-weight:800;color:#9B9B9B;letter-spacing:1.5px;text-transform:uppercase;margin:0 0 12px">
            Frequência
          </p>
          <div style="display:flex;flex-direction:column;gap:10px">
            ${_card('📊', 'Consultar Frequência', 'Visualize presença por turma e aluno', '/admin/frequencia', '#34c759')}
            ${_card('✍️', 'Lançar Frequência', 'Registre presença manualmente (rascunho local)', '/admin/lancar', '#ff9500')}
          </div>
        </div>

        <!-- Seção: Conteúdo e Dados -->
        <div style="margin-top:24px;margin-bottom:8px">
          <p style="font-size:11px;font-weight:800;color:#9B9B9B;letter-spacing:1.5px;text-transform:uppercase;margin:0 0 12px">
            Conteúdo e Dados
          </p>
          <div style="display:flex;flex-direction:column;gap:10px">
            ${_card('🎛️', 'Gerenciar Conteúdo', 'Controle visibilidade de cursos e avisos do portal', '/admin/conteudo', '#6A001F')}
            ${_card('📂', 'Importar Planilhas', 'Importe frequência, programação e presença (.xlsx)', '/admin/importar', '#007AFF')}
            ${_card('📜', 'Histórico de Ações', 'Consulte o log de auditoria (lançamentos e alterações)', '/admin/historico', '#8E8E93')}
          </div>
        </div>

        <!-- Seção: Financeiro -->
        <div style="margin-top:24px;margin-bottom:8px">
          <p style="font-size:11px;font-weight:800;color:#9B9B9B;letter-spacing:1.5px;text-transform:uppercase;margin:0 0 12px">
            Financeiro
          </p>
          <div style="display:flex;flex-direction:column;gap:10px">
            ${_card('💰', 'Financeiro (Admin)', 'Visão financeira por aluno — recurso experimental', '/admin/financeiro', '#8E8E93')}
          </div>
        </div>

        <!-- Rodapé informativo -->
        <div style="
          margin-top:32px;background:#fff;border-radius:14px;
          padding:16px;box-shadow:0 1px 6px rgba(0,0,0,0.05);
        ">
          <div style="font-size:11px;font-weight:700;color:#9B9B9B;text-transform:uppercase;letter-spacing:1px;margin-bottom:8px">
            Versão do sistema
          </div>
          <div style="font-size:13px;font-weight:600;color:#0A0A0A">App Eloquence v19.0.0</div>
          <div style="font-size:12px;color:#9B9B9B;margin-top:2px">
            Refatoração modular — branch <code>refactor/v19</code>
          </div>
        </div>
      </main>
    </div>
  `;

  // ── Eventos ─────────────────────────────────────────────────────────────────

  // Botão Atualizar — limpa caches e recarrega dashboard
  const refreshBtn = container.querySelector('#adminRefreshBtn');
  if (refreshBtn) {
    refreshBtn.addEventListener('click', async () => {
      if (refreshBtn.disabled) return;
      refreshBtn.disabled = true;
      refreshBtn.classList.add('is-spinning');
      const loadingId = notify.loading('Atualizando dados...');
      try {
        await api.forceRefreshAll();
        notify.hide(loadingId);
        notify.success('Dados atualizados');
        setTimeout(() => router.go('/admin'), 400);
      } catch (err) {
        notify.hide(loadingId);
        notify.error('Erro ao atualizar. Tente novamente.');
        console.error('[admin] forceRefreshAll falhou:', err);
      } finally {
        refreshBtn.disabled = false;
        refreshBtn.classList.remove('is-spinning');
      }
    });
  }

  // Botão logout
  container.querySelector('#adminLogoutBtn').addEventListener('click', () => {
    auth.logout();
    router.go('/login');
  });

  // Cards de navegação
  container.querySelectorAll('.admin-dash-card[data-rota]').forEach(btn => {
    // Hover visual
    btn.addEventListener('mouseenter', () => {
      btn.style.transform = 'translateY(-2px)';
      btn.style.boxShadow = '0 6px 20px rgba(0,0,0,0.12)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.transform = '';
      btn.style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)';
    });

    // Clique para navegar
    btn.addEventListener('click', () => {
      router.go(btn.dataset.rota);
    });
  });
}
